import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
} from "react";
import { XCancelIcon } from "../assets";

interface ErrorContextType {
  showError: (message: string) => void;
  hideError: () => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const autoCloseTime = 5000;

  // Error state management
  const [error, setError] = useState<ErrorState | null>(null);

  // Banner animation state
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const showError = useCallback((message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    setError({
      message,
      isVisible: true,
      id,
    });
  }, []);

  const hideError = useCallback(() => {
    setError((prev) => (prev ? { ...prev, isVisible: false } : null));
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    setIsAnimating(false);
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setShouldRender(false);
      clearError();
    }, 300);
  }, [clearError]);

  // Handle banner visibility and auto-close
  useEffect(() => {
    if (error?.isVisible) {
      setShouldRender(true);
      // Small delay to ensure the component is rendered before animation starts
      setTimeout(() => setIsAnimating(true), 10);

      // Auto-close timer
      const timer = setTimeout(() => {
        handleClose();
      }, autoCloseTime);

      return () => clearTimeout(timer);
    } else if (error) {
      handleClose();
    }
  }, [error?.isVisible, error?.id, autoCloseTime, handleClose]);

  const contextValue: ErrorContextType = {
    showError,
    hideError,
    clearError,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      {shouldRender && error && (
        <div className="fixed bottom-[67px] left-0 right-0 z-50 pointer-events-none flex items-center justify-center">
          <div
            className={`flex justify-between items-center gap-3 w-full max-w-[500px] h-[40px]
              bg-bet-builder-toaster-danger text-bet-builder-toaster-danger-text border border-solid border-bet-builder-toaster-danger-border
              px-4 rounded-6 shadow-lg mx-auto pointer-events-auto
              transform transition-all duration-300 ease-in-out
              ${
                isAnimating
                  ? "translate-y-0 opacity-100 scale-100"
                  : "translate-y-4 opacity-0 scale-95"
              }
            `}
            role="alert"
            aria-live="polite"
          >
            {/* Error Message */}
            <div className="flex-1 min-w-0 text-sm">{error.message}</div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="flex-shrink-0 rounded-6 focus:outline-none"
              aria-label="Close error message"
            >
              <i className="au-i-x-rounded-thin text-11"></i>
            </button>
          </div>
        </div>
      )}
    </ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
};
